import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fmtMoney, fmtNum, formatErr } from "@/lib/api";
import { mergeMetrics } from "@/lib/liveMetrics";
import { useWS } from "@/contexts/WebSocketContext";
import { useAuth } from "@/contexts/AuthContext";
import { LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { ArrowLeft, Lightning, Pause, Play, X, Warning, Power, Shield, TrendUp, TrendDown } from "@phosphor-icons/react";
import RefreshButton from "@/components/layout/RefreshButton";
import PageShell from "@/components/layout/PageShell";
import PageHeader from "@/components/layout/PageHeader";
import AccountIdentity from "@/components/AccountIdentity";
import GroupSelect from "@/components/GroupSelect";
import OrderModal from "@/components/OrderModal";
import HedgeSquareOffModal from "@/components/HedgeSquareOffModal";

const StatCard = ({ label, value, accent }) => (
  <div className="border border-app-border bg-app-surface p-4">
    <div className="font-mono-d text-[10px] uppercase tracking-widest text-app-subtle mb-2">{label}</div>
    <div className={`font-mono-d text-xl ${accent === "green" ? "text-[#10B981]" : accent === "red" ? "text-[#FF3B30]" : accent === "blue" ? "text-[#38BDF8]" : "text-app-text"}`}>{value}</div>
  </div>
);

export default function AccountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { liveMetrics, liveVersion, cmdTick } = useWS();
  const { user } = useAuth();
  const [acc, setAcc] = useState(null);
  const [trades, setTrades] = useState({ open: [], history: [] });
  const [equity, setEquity] = useState([]);
  const [daily, setDaily] = useState([]);
  const [groups, setGroups] = useState([]);
  const [error, setError] = useState(null);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [orderModalCommand, setOrderModalCommand] = useState("buy_order");

  const [hedgeModalOpen, setHedgeModalOpen] = useState(false);
  const [hedgeModalMode, setHedgeModalMode] = useState("single");
  const [selectedHedgeTrade, setSelectedHedgeTrade] = useState(null);
  const [selectedHedgeTrades, setSelectedHedgeTrades] = useState([]);
  const accountIds = useMemo(() => [id], [id]);

  const load = useCallback(async () => {
    try {
      const [a, t, e, d, g] = await Promise.all([
        api.get(`/accounts/${id}`),
        api.get(`/accounts/${id}/trades`),
        api.get(`/accounts/${id}/equity-curve`),
        api.get(`/accounts/${id}/daily-pl`),
        api.get("/groups"),
      ]);
      setAcc(a.data);
      setTrades(t.data);
      setEquity(e.data);
      setDaily(d.data);
      setGroups(g.data);
      setError(null);
    } catch (e) {
      const msg = formatErr(e.response?.data?.detail) || "Failed to load account";
      setError(msg);
      setAcc(null);
      toast.error(msg);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!cmdTick) return;
    (async () => {
      try {
        const t = await api.get(`/accounts/${id}/trades`);
        setTrades(t.data);
      } catch {}
    })();
  }, [id, cmdTick]);

  const metrics = useMemo(
    () => mergeMetrics(acc?.metrics, liveMetrics[id]),
    [acc?.metrics, liveMetrics, id, liveVersion],
  );

  const runCmd = async (command, params = {}, label = command) => {
    let execParams = { ...params };
    if (command === "hedge_custom" && !execParams.lot) {
      const inputLot = window.prompt("Enter custom lot size to hedge (e.g., 0.50):", "0.50");
      if (!inputLot) return;
      const parsedLot = parseFloat(inputLot);
      if (isNaN(parsedLot) || parsedLot <= 0) {
        return toast.error("Please enter a valid lot size greater than 0");
      }
      execParams.lot = parsedLot;
      label = `Custom Hedge (${parsedLot} lot${parsedLot === 1 ? "" : "s"})`;
    }

    if (!window.confirm(`Execute: ${label}?`)) return;
    try {
      const { data } = await api.post(`/accounts/${id}/command`, { command, params: execParams });
      toast.success(data.status === "pending" ? `${label} sent to EA` : `${label} executed`);
      await load();
    } catch (e) { toast.error("Failed"); }
  };

  const handleHedgeTrade = async (trade) => {
    const ticket = trade.ticket || trade.id;
    await runCmd("hedge_position", { ticket, symbol: trade.symbol, lot: trade.lot }, `Hedge Position #${ticket}`);
  };

  const manualRefresh = async () => {
    await load();
    toast.success("Account data refreshed");
  };

  const canControl = user?.role !== "viewer";
  const m = metrics;

  const assignGroup = async (group) => {
    try {
      await api.patch(`/accounts/${id}`, { group: group || null });
      await load();
      toast.success(group ? `Assigned to ${group}` : "Group cleared");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to update group");
    }
  };

  if (!acc) return (
    <PageShell>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button onClick={() => navigate("/accounts")} className="btn-touch flex items-center gap-2 text-xs text-app-subtle hover:text-app-text font-mono-d uppercase tracking-widest">
          <ArrowLeft size={12} /> Accounts
        </button>
        <RefreshButton onRefresh={manualRefresh} testId="account-refresh-btn" />
      </div>
      <div className="text-app-muted font-mono-d text-xs">{error || "LOADING..."}</div>
    </PageShell>
  );

  return (
    <PageShell>
      <button onClick={() => navigate("/accounts")} data-testid="back-btn" className="btn-touch inline-flex items-center gap-2 text-xs text-app-subtle hover:text-app-text mb-4 font-mono-d uppercase tracking-widest">
        <ArrowLeft size={12} /> Accounts
      </button>

      <PageHeader
        eyebrow={`${acc.broker} · ${acc.currency} · 1:${acc.leverage}`}
        title={
          <AccountIdentity
            group={acc.group}
            groupColor={acc.group_color}
            accountNumber={acc.account_number}
            accountName={acc.account_name}
            large
            bold
          />
        }
      >
        <div className="flex flex-col items-stretch sm:items-end gap-2.5 w-full lg:w-auto">
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            {m.is_hedged && (
              <span className="px-2.5 py-1 font-mono-d text-[10px] uppercase tracking-widest border border-[#38BDF8]/40 text-[#38BDF8] bg-[#38BDF8]/8">
                Hedged ({fmtNum(m.hedge_lots ?? 0)} L)
              </span>
            )}
            <span className={`px-2.5 py-1 font-mono-d text-[10px] uppercase tracking-widest border ${m.vps_online ? "border-[#10B981]/40 text-[#10B981] bg-[#10B981]/8" : "border-[#FF3B30]/40 text-[#FF3B30] bg-[#FF3B30]/8"}`} data-testid="vps-status">
              VPS {m.vps_online ? "Online" : "Offline"} · {m.ping ?? 0}ms
            </span>
            <span className={`px-2.5 py-1 font-mono-d text-[10px] uppercase tracking-widest border ${
              !m.ea_connected ? "border-app-border text-app-muted bg-app-surface" :
              m.ea_paused ? "border-[#FF9500]/40 text-[#FF9500] bg-[#FF9500]/8" :
              !m.entries_enabled ? "border-[#FF9500]/40 text-[#FF9500] bg-[#FF9500]/8" :
              m.ea_running ? "border-[#10B981]/40 text-[#10B981] bg-[#10B981]/8" : "border-[#FF9500]/40 text-[#FF9500] bg-[#FF9500]/8"
            }`} data-testid="ea-status">
              EA {!m.ea_connected ? "Offline" : m.ea_paused ? "Paused" : !m.entries_enabled ? "Entries off" : "Running"}
            </span>
          </div>
          <RefreshButton onRefresh={manualRefresh} testId="account-refresh-btn" />
        </div>
      </PageHeader>

      {canControl && (
        <div className="page-filter-bar mb-6">
          <div className="page-filter-field w-full sm:w-52 lg:w-56">
            <label htmlFor="account-group" className="page-filter-label">Group</label>
            <GroupSelect
              value={acc.group}
              groups={groups}
              onChange={assignGroup}
              className="w-full"
            />
          </div>
          <div className="page-filter-field flex-1 min-w-0">
            <span className="page-filter-label">Account</span>
            <div className="flex items-center min-h-[40px] px-3 border border-app-border bg-app-bg font-mono-d text-sm text-app-text">
              #{acc.account_number} · {acc.account_name || "—"}
            </div>
          </div>
          <div className="page-filter-field w-full sm:w-36 lg:w-40">
            <span className="page-filter-label">Status</span>
            <div className={`flex items-center min-h-[40px] px-3 border font-mono-d text-xs uppercase tracking-widest ${
              acc.enabled ? "border-[#10B981]/40 text-[#10B981] bg-[#10B981]/8" : "border-[#FF3B30]/40 text-[#FF3B30] bg-[#FF3B30]/8"
            }`}>
              {acc.enabled ? "Active" : "Disabled"}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatCard label="Start Bal" value={fmtMoney(m.starting_balance ?? m.balance)} />
        <StatCard label="Close Bal" value={fmtMoney(m.closing_balance ?? m.balance)} />
        <StatCard label="Max DD (Today)" value={`${fmtNum(m.max_drawdown_today ?? m.drawdown)}%`} accent={(m.max_drawdown_today ?? m.drawdown) > 8 ? "red" : null} />
        <StatCard label="Profit (Today)" value={fmtMoney(m.profit_today ?? m.daily_pl)} accent={(m.profit_today ?? m.daily_pl) >= 0 ? "green" : "red"} />
        <StatCard label="Balance" value={fmtMoney(m.balance)} />
        <StatCard label="Equity" value={fmtMoney(m.equity)} />
        <StatCard label="Floating P/L" value={fmtMoney(m.floating_pl)} accent={m.floating_pl >= 0 ? "green" : "red"} />
        <StatCard label="Withdrawals" value={fmtMoney(m.withdrawals_today ?? 0)} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="border border-app-border bg-app-surface p-4">
          <div className="font-mono-d text-[10px] uppercase tracking-widest text-app-subtle mb-3">Trading Metrics</div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-app-muted">Win Rate</span><span className="font-mono-d">{fmtNum(m.win_rate, 1)}%</span></div>
            <div className="flex justify-between"><span className="text-app-muted">Total Lots</span><span className="font-mono-d">{fmtNum(m.total_lots)}</span></div>
            <div className="flex justify-between"><span className="text-app-muted">Open Positions</span><span className="font-mono-d">{m.open_positions ?? 0}</span></div>
            <div className="flex justify-between"><span className="text-app-muted">Last Trade</span><span className="font-mono-d text-xs">{m.last_trade_time ? new Date(m.last_trade_time).toLocaleString() : "—"}</span></div>
            <div className="flex justify-between"><span className="text-app-muted">Last Signal</span><span className="font-mono-d text-xs">{m.last_signal_time ? new Date(m.last_signal_time).toLocaleString() : "—"}</span></div>
          </div>
        </div>
        <div className="border border-app-border bg-app-surface p-4">
          <div className="font-mono-d text-[10px] uppercase tracking-widest text-app-subtle mb-3">EA Status</div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-app-muted">Connected</span><span className={`font-mono-d ${m.ea_connected ? "text-[#10B981]" : "text-app-muted"}`}>{m.ea_connected ? "Yes" : "No"}</span></div>
            <div className="flex justify-between"><span className="text-app-muted">Running</span><span className={`font-mono-d ${m.ea_running ? "text-[#10B981]" : "text-[#FF3B30]"}`}>{m.ea_running ? "Yes" : "No"}</span></div>
            <div className="flex justify-between"><span className="text-app-muted">Paused</span><span className={`font-mono-d ${m.ea_paused ? "text-[#FF9500]" : "text-app-muted"}`}>{m.ea_paused ? "Yes" : "No"}</span></div>
            <div className="flex justify-between"><span className="text-app-muted">Entries</span><span className={`font-mono-d ${m.entries_enabled ? "text-[#10B981]" : "text-[#FF9500]"}`}>{m.entries_enabled ? "Enabled" : "Disabled"}</span></div>
          </div>
        </div>
        <div className="border border-app-border bg-app-surface p-4">
          <div className="font-mono-d text-[10px] uppercase tracking-widest text-app-subtle mb-3">VPS Metrics</div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-app-muted">Status</span><span className={`font-mono-d ${m.vps_online ? "text-[#10B981]" : "text-[#FF3B30]"}`}>{m.vps_online ? "Online" : "Offline"}</span></div>
            <div className="flex justify-between"><span className="text-app-muted">Ping</span><span className="font-mono-d">{m.ping ?? 0}ms</span></div>
            <div className="flex justify-between"><span className="text-app-muted">CPU</span><span className="font-mono-d">{fmtNum(m.cpu_usage, 1)}%</span></div>
            <div className="flex justify-between"><span className="text-app-muted">RAM</span><span className="font-mono-d">{fmtNum(m.ram_usage, 1)}%</span></div>
            <div className="flex justify-between"><span className="text-app-muted">Disk</span><span className="font-mono-d">{fmtNum(m.disk_usage, 1)}%</span></div>
            <div className="flex justify-between"><span className="text-app-muted">Source</span><span className="font-mono-d text-xs uppercase">{m.source || "—"}</span></div>
          </div>
        </div>
        <div className="border border-app-border bg-app-surface p-4">
          <div className="font-mono-d text-[10px] uppercase tracking-widest text-app-subtle mb-3">Hedge Management</div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-app-muted">Hedged</span><span className={`font-mono-d ${m.is_hedged ? "text-[#38BDF8]" : "text-app-muted"}`}>{m.is_hedged ? "Yes" : "No"}</span></div>
            <div className="flex justify-between"><span className="text-app-muted">Hedge Lots</span><span className="font-mono-d">{fmtNum(m.hedge_lots ?? 0)}</span></div>
            <div className="flex justify-between"><span className="text-app-muted">Hedge Pos</span><span className="font-mono-d">{m.hedge_positions ?? 0}</span></div>
            <div className="flex justify-between"><span className="text-app-muted">Hedge MTM</span><span className={`font-mono-d font-semibold ${(m.hedge_mtm ?? 0) >= 0 ? "text-[#10B981]" : "text-[#FF3B30]"}`}>{fmtMoney(m.hedge_mtm ?? 0)}</span></div>
            <div className="flex justify-between"><span className="text-app-muted">Auto-Hedge</span><span className={`font-mono-d ${m.auto_hedge_enabled ? "text-[#10B981]" : "text-app-muted"}`}>{m.auto_hedge_enabled ? "Enabled" : "Disabled"}</span></div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6 min-w-0">
        <div className="border border-app-border bg-app-surface p-4 chart-panel">
          <div className="font-mono-d text-[10px] uppercase tracking-widest text-app-subtle mb-4">Equity Curve · 30d</div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={equity}>
              <CartesianGrid strokeDasharray="2 4" stroke="#2a2a2a" />
              <XAxis dataKey="timestamp" tick={{ fill: "#666", fontSize: 9 }} tickFormatter={(t) => new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric" })} />
              <YAxis tick={{ fill: "#666", fontSize: 9 }} />
              <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #2a2a2a", fontFamily: "IBM Plex Mono", fontSize: 11 }} />
              <Line type="monotone" dataKey="equity" stroke="#10B981" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="border border-app-border bg-app-surface p-4 chart-panel">
          <div className="font-mono-d text-[10px] uppercase tracking-widest text-app-subtle mb-4">Daily Performance · 14d</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={daily}>
              <CartesianGrid strokeDasharray="2 4" stroke="#2a2a2a" />
              <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 9 }} />
              <YAxis tick={{ fill: "#666", fontSize: 9 }} />
              <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #2a2a2a", fontFamily: "IBM Plex Mono", fontSize: 11 }} />
              <Bar dataKey="profit">
                {daily.map((d, i) => (
                  <Cell key={i} fill={d.profit >= 0 ? "#10B981" : "#FF3B30"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {canControl && (
        <div className="border border-app-border bg-app-surface p-6 mb-6" data-testid="command-panel">
          <div className="flex items-center gap-2 mb-4">
            <Lightning size={14} className="text-[#007AFF]" />
            <div className="font-mono-d text-[10px] uppercase tracking-[0.25em] text-app-subtle">Command Center</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <button onClick={() => { setOrderModalCommand("buy_order"); setOrderModalOpen(true); }} data-testid="cmd-buy-order" className="btn-touch px-3 py-2.5 text-xs bg-[#10B981] hover:bg-[#059669] text-white font-bold flex items-center justify-center gap-1.5"><TrendUp size={14} weight="bold" /> Buy Order…</button>
            <button onClick={() => { setOrderModalCommand("sell_order"); setOrderModalOpen(true); }} data-testid="cmd-sell-order" className="btn-touch px-3 py-2.5 text-xs bg-[#FF3B30] hover:bg-[#DC2626] text-white font-bold flex items-center justify-center gap-1.5"><TrendDown size={14} weight="bold" /> Sell Order…</button>
            <button onClick={() => { setOrderModalCommand("custom_lots"); setOrderModalOpen(true); }} data-testid="cmd-custom-order" className="btn-touch px-3 py-2.5 text-xs border border-app-accent/40 bg-app-accent-soft text-app-accent font-semibold flex items-center justify-center gap-1.5"><Lightning size={12} weight="bold" /> Custom Lot…</button>
            <button onClick={() => runCmd("close_all", {}, "Close All Trades")} data-testid="cmd-close-all" className="btn-touch px-3 py-2.5 text-xs bg-[#FF3B30] hover:bg-[#FF5C53] flex items-center justify-center gap-1.5"><X size={12} weight="bold" /> Close All</button>
            <button onClick={() => runCmd("close_buy", {}, "Close Buy Trades")} data-testid="cmd-close-buy" className="btn-touch px-3 py-2.5 text-xs border border-app-border hover:bg-app-hover">Close Buys</button>
            <button onClick={() => runCmd("close_sell", {}, "Close Sell Trades")} data-testid="cmd-close-sell" className="btn-touch px-3 py-2.5 text-xs border border-app-border hover:bg-app-hover">Close Sells</button>
            <button onClick={() => runCmd("emergency_close", {}, "EMERGENCY CLOSE ALL")} data-testid="cmd-emergency" className="btn-touch px-3 py-2.5 text-xs bg-[#FF3B30] hover:bg-[#FF5C53] flex items-center justify-center gap-1.5"><Warning size={12} weight="bold" /> Emergency</button>
            <button onClick={() => runCmd("hedge_account", {}, "Full Hedge Account")} data-testid="cmd-hedge-account" className="btn-touch px-3 py-2.5 text-xs border border-app-border hover:bg-app-hover flex items-center justify-center gap-1.5"><Shield size={12} weight="bold" /> Full Hedge</button>
            <button onClick={() => { setSelectedHedgeTrade(null); setHedgeModalMode("all"); setHedgeModalOpen(true); }} data-testid="cmd-close-hedges" className="btn-touch px-3 py-2.5 text-xs border border-[#38BDF8]/40 text-[#38BDF8] hover:bg-[#38BDF8]/10 font-bold flex items-center justify-center gap-1.5"><Shield size={12} weight="bold" /> Square Off Hedges</button>
            <button onClick={() => runCmd("pause_ea", {}, "Pause EA")} data-testid="cmd-pause" className="btn-touch px-3 py-2.5 text-xs border border-app-border hover:bg-app-hover flex items-center justify-center gap-1.5"><Pause size={12} weight="fill" /> Pause EA</button>
            <button onClick={() => runCmd("resume_ea", {}, "Resume EA")} data-testid="cmd-resume" className="btn-touch px-3 py-2.5 text-xs border border-app-border hover:bg-app-hover flex items-center justify-center gap-1.5"><Play size={12} weight="fill" /> Resume EA</button>
            <button onClick={() => runCmd("disable_entries", {}, "Disable New Entries")} data-testid="cmd-disable-entries" className="btn-touch px-3 py-2.5 text-xs border border-app-border hover:bg-app-hover">Disable Entries</button>
            <button onClick={() => runCmd("enable_entries", {}, "Enable New Entries")} data-testid="cmd-enable-entries" className="btn-touch px-3 py-2.5 text-xs border border-app-border hover:bg-app-hover">Enable Entries</button>
            <button onClick={() => runCmd("disable_trading", {}, "Disable Trading")} data-testid="cmd-disable-trading" className="btn-touch px-3 py-2.5 text-xs border border-[#FF9500]/40 text-[#FF9500] hover:bg-[#FF9500]/10">Disable Trading</button>
            <button onClick={() => runCmd("kill_switch", {}, "KILL SWITCH")} data-testid="cmd-kill" className="btn-touch px-3 py-2.5 text-xs bg-[#FF3B30] hover:bg-[#FF5C53] flex items-center justify-center gap-1.5 lg:col-span-2"><Power size={12} weight="bold" /> Kill Switch</button>
          </div>
          <p className="mt-3 text-[10px] font-mono-d text-app-muted leading-relaxed space-y-2">
            <span className="block"><span className="text-app-subtle">Buy / Sell Orders</span> place instant market orders with configured lot size, SL, TP, and Trailing SL (in pips).</span>
            <span className="block"><span className="text-app-subtle">Pause EA</span> stops all EA execution.</span>
            <span className="block"><span className="text-app-subtle">Kill Switch</span> closes all positions and disables the account.</span>
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 min-w-0">
        <div className="border border-app-border bg-app-surface p-4 min-w-0">
          <div className="font-mono-d text-[10px] uppercase tracking-widest text-app-subtle mb-3">Open Positions · {trades.open.length}</div>

          <div className="lg:hidden data-card-list">
            {trades.open.map(t => {
              const isHedge = Boolean(t.is_hedge || t.magic === 90001 || String(t.magic) === "90001" || t.magic === 999999 || String(t.magic) === "999999" || ["HEDGE", "HDG", "HUB"].some(kw => String(t.comment || "").toUpperCase().includes(kw)));
              return (
                <div key={t.id} className="data-card" data-testid={`open-pos-card-${t.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5">
                      <span className="font-mono-d text-sm">{t.symbol}</span>
                      <span className={`px-1.5 font-mono-d text-[10px] uppercase ${t.side === "buy" ? "text-[#10B981]" : "text-[#FF3B30]"}`}>{t.side}</span>
                      {isHedge && (
                        <span className="px-1 py-0.2 rounded bg-[#0284C7]/20 border border-[#0284C7]/40 font-mono-d text-[9px] text-[#38BDF8] uppercase tracking-wider">HEDGE</span>
                      )}
                    </div>
                    {canControl && (
                      isHedge ? (
                        <button
                          onClick={() => { setSelectedHedgeTrade({ ...t, account_id: id }); setHedgeModalMode("single"); setHedgeModalOpen(true); }}
                          className="px-2 py-0.5 text-[10px] font-mono-d uppercase tracking-wider border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 flex items-center gap-1 font-semibold"
                          title="Square Off Hedge Position"
                        >
                          <X size={10} weight="bold" /> Square Off
                        </button>
                      ) : (
                        <button
                          onClick={() => handleHedgeTrade(t)}
                          className="px-2 py-0.5 text-[10px] font-mono-d uppercase tracking-wider border border-[#38BDF8]/40 text-[#38BDF8] hover:bg-[#38BDF8]/10 flex items-center gap-1"
                          title="Hedge Position"
                        >
                          <Shield size={10} /> Hedge
                        </button>
                      )
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="data-card-row"><span className="data-card-label">Lot</span><span className="font-mono-d">{fmtNum(t.lot)}</span></div>
                    <div className="data-card-row"><span className="data-card-label">Entry</span><span className="font-mono-d">{fmtNum(t.entry_price, 4)}</span></div>
                    <div className="data-card-row"><span className="data-card-label">Current</span><span className="font-mono-d">{fmtNum(t.current_price, 4)}</span></div>
                    <div className="data-card-row"><span className="data-card-label">P/L</span><span className={`font-mono-d ${t.profit >= 0 ? "text-[#10B981]" : "text-[#FF3B30]"}`}>{fmtMoney(t.profit)}</span></div>
                  </div>
                </div>
              );
            })}
            {trades.open.length === 0 && <div className="py-6 text-center text-app-muted font-mono-d text-xs">NO OPEN POSITIONS</div>}
          </div>

          <div className="hidden lg:block table-scroll">
          <table className="w-full text-sm" data-testid="open-positions">
            <thead><tr className="text-left border-b border-app-border">
              {["Symbol", "Side", "Lot", "Entry", "Current", "P/L", "Actions"].map(h => <th key={h} className="px-2 py-2 font-mono-d text-[10px] text-app-subtle uppercase tracking-widest">{h}</th>)}
            </tr></thead>
            <tbody>
              {trades.open.map(t => {
                const isHedge = Boolean(t.is_hedge || t.magic === 90001 || String(t.magic) === "90001" || t.magic === 999999 || String(t.magic) === "999999" || ["HEDGE", "HDG", "HUB"].some(kw => String(t.comment || "").toUpperCase().includes(kw)));
                return (
                  <tr key={t.id} className="border-b border-app-border/70">
                    <td className="px-2 py-2 font-mono-d">{t.symbol}</td>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-1.5">
                        <span className={`px-1.5 font-mono-d text-[10px] uppercase ${t.side === "buy" ? "text-[#10B981]" : "text-[#FF3B30]"}`}>{t.side}</span>
                        {isHedge && <span className="px-1 py-0.2 rounded bg-[#0284C7]/20 border border-[#0284C7]/40 font-mono-d text-[9px] text-[#38BDF8] uppercase tracking-wider">HEDGE</span>}
                      </div>
                    </td>
                    <td className="px-2 py-2 font-mono-d">{fmtNum(t.lot)}</td>
                    <td className="px-2 py-2 font-mono-d">{fmtNum(t.entry_price, 4)}</td>
                    <td className="px-2 py-2 font-mono-d">{fmtNum(t.current_price, 4)}</td>
                    <td className={`px-2 py-2 font-mono-d ${t.profit >= 0 ? "text-[#10B981]" : "text-[#FF3B30]"}`}>{fmtMoney(t.profit)}</td>
                    <td className="px-2 py-2">
                      {canControl && (
                        isHedge ? (
                          <button
                            onClick={() => { setSelectedHedgeTrade({ ...t, account_id: id }); setHedgeModalMode("single"); setHedgeModalOpen(true); }}
                            className="px-2 py-0.5 text-[10px] font-mono-d uppercase border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 flex items-center gap-1 font-semibold"
                            title="Square Off Hedge Position"
                          >
                            <X size={10} weight="bold" /> Square Off
                          </button>
                        ) : (
                          <button
                            onClick={() => handleHedgeTrade(t)}
                            className="p-1 hover:text-[#38BDF8] transition-colors"
                            title="Hedge Position"
                          >
                            <Shield size={14} />
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
              {trades.open.length === 0 && <tr><td colSpan={7} className="px-2 py-6 text-center text-app-muted font-mono-d text-xs">NO OPEN POSITIONS</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
        <div className="border border-app-border bg-app-surface p-4 min-w-0">
          <div className="font-mono-d text-[10px] uppercase tracking-widest text-app-subtle mb-3">Trade History · {trades.history.length}</div>

          <div className="lg:hidden max-h-72 overflow-auto data-card-list">
            {trades.history.map(t => (
              <div key={`${t.id}-${t.closed_at}`} className="data-card">
                <div className="font-mono-d text-[10px] text-app-muted mb-2">{t.closed_at ? new Date(t.closed_at).toLocaleString() : "—"}</div>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono-d text-sm">{t.symbol}</span>
                  <span className={`font-mono-d text-[11px] uppercase ${t.side === "buy" ? "text-[#10B981]" : "text-[#FF3B30]"}`}>{t.side}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="data-card-row"><span className="data-card-label">Lot</span><span className="font-mono-d">{fmtNum(t.lot)}</span></div>
                  <div className="data-card-row"><span className="data-card-label">Profit</span><span className={`font-mono-d ${t.profit >= 0 ? "text-[#10B981]" : "text-[#FF3B30]"}`}>{fmtMoney(t.profit)}</span></div>
                </div>
              </div>
            ))}
            {trades.history.length === 0 && <div className="py-6 text-center text-app-muted font-mono-d text-xs">NO CLOSED TRADES YET</div>}
          </div>

          <div className="hidden lg:block max-h-72 overflow-auto">
            <table className="w-full text-sm" data-testid="trade-history">
              <thead><tr className="text-left border-b border-app-border">
                {["Closed", "Symbol", "Side", "Lot", "Profit"].map(h => <th key={h} className="px-2 py-2 font-mono-d text-[10px] text-app-subtle uppercase tracking-widest">{h}</th>)}
              </tr></thead>
              <tbody>
                {trades.history.map(t => (
                  <tr key={`${t.id}-${t.closed_at}`} className="border-b border-app-border/70">
                    <td className="px-2 py-2 font-mono-d text-xs">{t.closed_at ? new Date(t.closed_at).toLocaleString() : "—"}</td>
                    <td className="px-2 py-2 font-mono-d">{t.symbol}</td>
                    <td className={`px-2 py-2 font-mono-d text-[11px] uppercase ${t.side === "buy" ? "text-[#10B981]" : "text-[#FF3B30]"}`}>{t.side}</td>
                    <td className="px-2 py-2 font-mono-d">{fmtNum(t.lot)}</td>
                    <td className={`px-2 py-2 font-mono-d ${t.profit >= 0 ? "text-[#10B981]" : "text-[#FF3B30]"}`}>{fmtMoney(t.profit)}</td>
                  </tr>
                ))}
                {trades.history.length === 0 && <tr><td colSpan={5} className="px-2 py-6 text-center text-app-muted font-mono-d text-xs">NO CLOSED TRADES YET</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <OrderModal
        isOpen={orderModalOpen}
        initialCommand={orderModalCommand}
        selectedCount={1}
        onClose={() => setOrderModalOpen(false)}
        onConfirm={async (cmd, params, label) => {
          setOrderModalOpen(false);
          await runCmd(cmd, params, label);
        }}
      />

      <HedgeSquareOffModal
        isOpen={hedgeModalOpen}
        mode={hedgeModalMode}
        trade={selectedHedgeTrade}
        selectedTrades={selectedHedgeTrades}
        selectedAccountIds={accountIds}
        allTrades={trades.open}
        activeHedgesCount={m.hedge_positions ?? 0}
        totalHedgeLots={m.hedge_lots ?? 0}
        overallHedgeMtm={m.hedge_mtm ?? 0}
        onClose={() => setHedgeModalOpen(false)}
        onSuccess={load}
      />
    </PageShell>
  );
}
